import { ALL_PERMISSIONS } from '../constants/permissionsConstants';

// Compute the effective permission set for the current user.
//
// Bidlight pattern: the user's effective perms are the intersection of
//   - allPermission     — every permission the user's licensed packages enable
//   - companyPermission — what the company has granted at the tenant level
//   - userPermission    — what's been granted to this specific user
//
// Super-admin bypasses every gate (matches the `requireSuper`/proxy
// `SUPERADMIN_ONLY` behavior on the backend). Their `userPermission`
// row is usually empty AND their company often has no licensed
// packages, so any subset of `allPermission` was empty — the sidebar
// and settings pages rendered every entry behind "Request Access" /
// "Acquire License" badges. Resolve directly to the full ALL_PERMISSIONS
// dictionary so super-admin sees every affordance regardless of
// license state, matching what the backend would actually accept.
const ALL_PERM_VALUES = Object.freeze(Object.values(ALL_PERMISSIONS));

// Three-tier resolution, in order:
//   1. superAdmin → every permission in ALL_PERMISSIONS, regardless
//      of company license. Mirrors the backend requireSuper bypass.
//   2. companyAdmin → every permission the company has
//      (companyPermission). A tenant admin is unrestricted within
//      what their license allows; nobody needs to seed a user-level
//      Permission row for them. Empirically the user-perm row was
//      always empty for companyAdmins, which was making the
//      intersection collapse to [] and the entire admin sidebar
//      render behind "Acquire License" badges.
//   3. Everyone else → allPermission ∩ companyPermission ∩
//      userPermission (per-user grants restrict what the company
//      has licensed).
export function computeFinalPermissions({
  userInfo,
  allPermission,
  companyPermission,
  userPermission,
}) {
  if (userInfo && userInfo.superAdmin) {
    return ALL_PERM_VALUES.slice();
  }
  if (userInfo && userInfo.companyAdmin) {
    return Array.isArray(companyPermission) ? [...companyPermission] : [];
  }
  const all = Array.isArray(allPermission) ? allPermission : [];
  return all
    .filter((p) => (companyPermission || []).includes(p))
    .filter((p) => (userPermission || []).includes(p));
}

// Does the user have access to the admin area at all?
//
// "Admin access" is the gate for the whole admin surface — the admin side
// menu (Super Admin + Admin sections) AND the /admin/* URLs. It must be
// enforced exactly like any other feature: a user without it neither sees
// the admin menu nor can navigate to an admin page (the route falls through
// to the personal-area redirect, same as any unlicensed feature).
//
// Three ways in, mirroring the backend `checkWorkerAccess`
// (worker-system/routes/workerRoutes.js):
//   1. superAdmin   — platform operator, bypasses everything.
//   2. companyAdmin — tenant admin, always has admin access.
//   3. anyone holding the ADMIN_SECTION_ACCESS ("adminSectionAccess")
//      permission grant.
// Pass `finalPermissions` (the resolved set from computeFinalPermissions) so
// the per-user / per-company / package intersection is already applied.
export function hasAdminAccess({ userInfo, finalPermissions } = {}) {
  if (userInfo && (userInfo.superAdmin || userInfo.companyAdmin)) return true;
  const perms = Array.isArray(finalPermissions) ? finalPermissions : [];
  return perms.includes(ALL_PERMISSIONS.ADMIN_SECTION_ACCESS);
}
